import { gql } from "lib/apolloClient";
import { messageFragment, userFragment } from "./MessagingFragments";
import { channelDetailsFragment } from "../components/ThreadList/Utils/ThreadListQueries";

export const deleteMessageV2Mutation = gql`
  mutation deleteMessageV2($input: MessageDeleteInput!) {
    platform {
      deleteMessageV2(input: $input)
    }
  }
`;

export const createMessageReactionMutation = gql`
  mutation createMessageReaction($input: MessageReactionCreateInput!) {
    platform {
      createMessageReaction(input: $input) {
        id
      }
    }
  }
`;

export const removeMessageReactionMutation = gql`
  mutation removeMessageReaction($input: MessageReactionRemoveInput!) {
    platform {
      removeMessageReaction(input: $input) {
        id
      }
    }
  }
`;

export const toggleChatMuteMutation = gql`
  mutation toggleChatMuteMutation($id: ID!) {
    platform {
      toggleChatMute(input: { chatId: $id }) {
        ... on Chat {
          id
          isMuted
        }
      }
    }
  }
`;

export const updateChannelMutation = gql`
  mutation updateCustomChannel(
    $input: CustomChannelUpdateInput!
    $organizationId: ID!
  ) {
    platform {
      updateCustomChannel(input: $input) {
        ... on Chat {
          id
          type
          label
          description
          profileImageData {
            url
            icon
          }
          isMuted
          isArchived
          unreadMessageCount
          totalUsersCount
          viewOnlyRoles
          users {
            ...userDetailItem
          }
        }
      }
    }
  }
  ${userFragment.userDetailItem}
`;

export const leaveChannelMutation = gql`
  mutation leaveChannel($input: LeaveChannelInput!) {
    platform {
      leaveChannel(input: $input) {
        id
      }
    }
  }
`;

export const joinChannelMutation = gql`
  mutation joinChannel($input: JoinChannelInput!, $organizationId: ID!) {
    platform {
      joinChannel(input: $input) {
        ... on Chat {
          id
          type
          label
          description
          profileImageData {
            url
            icon
          }
          isMuted
          isArchived
          unreadMessageCount
          totalUsersCount
          viewOnlyRoles
          users {
            ...userDetailItem
          }
        }
      }
    }
  }
  ${userFragment.userDetailItem}
`;

export const createChannelMutation = gql`
  mutation createCustomChannel(
    $input: CustomChannelCreateInput!
    $organizationId: ID!
  ) {
    platform {
      createCustomChannel(input: $input) {
        ... on Chat {
          ...chatDetailsItem
        }
      }
    }
  }
  ${channelDetailsFragment}
`;

export const updateChannelSettingMutation = gql`
  mutation updateChannelSettings($input: ChannelSettingsInput!) {
    platform {
      updateChannelSettings(input: $input) {
        ... on Chat {
          id
          label
          isArchived
          unreadMessageCount
          isMuted
          memberListHiddenRoles
          type
          viewOnlyRoles
        }
      }
    }
  }
`;

export const createPersonalChatMutation = gql`
  mutation createPersonalChat($input: PersonalChatCreateInput!) {
    platform {
      createPersonalChat(input: $input) {
        ... on Chat {
          id
          lastActiveAt
        }
      }
    }
  }
`;

export const createFamilyChannelMutation = gql`
  mutation createFamilyChannel($input: FamilyChannelCreateInput!) {
    platform {
      createFamilyChannel(input: $input) {
        ... on Chat {
          id
        }
      }
    }
  }
`;

export const createMessageMutation = gql`
  mutation createMessage(
    $createMessageV2Input: CreateMessageV2Input!
    $organizationId: ID!
  ) {
    platform {
      createMessage: createMessageV3(input: $createMessageV2Input) {
        ... on MessageV2 {
          ...messageV2ItemDetail
        }
      }
    }
  }
  ${messageFragment.messageV2ItemDetail}
`;

export const createFlaggedConversationSummaryMutation = gql`
  mutation createFlaggedConversationSummary(
    $input: CreateConversationSummaryInput!
  ) {
    platform {
      createConversationSummary(input: $input) {
        ... on MessageV2 {
          id
          type
          label
        }
      }
    }
  }
`;

export const createConversationSummaryMutation = gql`
  mutation createConversationSummary(
    $input: CreateConversationSummaryInput!
    $organizationId: ID!
  ) {
    platform {
      createConversationSummary(input: $input) {
        ... on MessageV2 {
          ...messageV2ItemDetail
        }
      }
    }
  }

  ${messageFragment.messageV2ItemDetail}
`;

export const initiateSentimentAnalysisMutation = gql`
  mutation initiateSentimentAnalysis {
    platform {
      initiateMessagingAISentimentAnalysis
    }
  }
`;

export const updateMessageMutation = gql`
  mutation updateMessage(
    $updateMessageV2Input: UpdateMessageV2Input!
    $organizationId: ID!
  ) {
    platform {
      updateMessage: updateMessageV2(input: $updateMessageV2Input) {
        ... on MessageV2 {
          ...messageV2ItemDetail
        }
      }
    }
  }
  ${messageFragment.messageV2ItemDetail}
`;

export const markMessageV2ReadMutation = gql`
  mutation markMessageV2Read($messageId: ID, $chatId: ID!) {
    platform {
      markMessageV2Read(input: { messageId: $messageId, chatId: $chatId }) {
        id
        isRead
        cursor
      }
    }
  }
`;

export const insertUserChatActivityLogMutation = gql`
  mutation insertUserChatActivityLog(
    $input: UserConversationActivityLogInput!
  ) {
    platform {
      addUserChatActivityLog(input: $input)
    }
  }
`;

export const markMessageV3ReadMutation = gql`
  mutation markMessageV3Read($input: MarkMessageV3ReadInput!) {
    platform {
      markMessageV3Read(input: $input)
    }
  }
`;

export const createCustomChannelAndMoveMessagesMutation = gql`
  mutation createCustomChannelAndMoveMessages(
    $input: CustomChannelCreateAndMoveMessagesInput!
  ) {
    platform {
      createCustomChannelAndMoveMessages(input: $input) {
        id
        label
      }
    }
  }
`;

export const forwardMessageMutation = gql`
  mutation forwardMessage($input: ForwardMessageInput!) {
    platform {
      forwardMessage(input: $input)
    }
  }
`;

export const createDepartmentChannelMutation = gql`
  mutation createDepartmentChannel($input: DepartmentChannelCreateInput!) {
    platform {
      createDepartmentChannel(input: $input) {
        id
        isArchived
      }
    }
  }
`;

export const updateDepartmentChannelMutation = gql`
  mutation updateDepartmentChannel(
    $input: DepartmentChannelUpdateInput!
    $organizationId: ID!
  ) {
    platform {
      updateDepartmentChannel(input: $input) {
        ... on Chat {
          id
          type
          label
          description
          profileImage
          isMuted
          isArchived
          unreadMessageCount
          totalUsersCount
          viewOnlyRoles
          users {
            ...userDetailItem
          }
        }
      }
    }
  }
  ${userFragment.userDetailItem}
`;

// messaging poll
export const createPollMessageMutation = gql`
  mutation createMessageV2Poll(
    $input: CreateMessageV2PollInput!
    $interactionMessageInput: InteractionMessageInput!
    $organizationId: ID!
  ) {
    platform {
      createMessageV2Poll(input: $input) {
        id
        messages(input: $interactionMessageInput) {
          edges {
            node {
              ... on MessageV2 {
                ...messageV2ItemDetail
              }
            }
          }
        }
      }
    }
  }
  ${messageFragment.messageV2ItemDetail}
`;

export const createPollResponseMutation = gql`
  mutation createPollResponse($input: CreatePollResponseInput!) {
    platform {
      createPollResponse(input: $input) {
        id
      }
    }
  }
`;

export const deleteInteractionMutation = gql`
  mutation deleteInteraction($input: DeleteInteractionInput!) {
    platform {
      deleteInteraction(input: $input)
    }
  }
`;

export const duplicateInteractionMutation = gql`
  mutation duplicateInteraction($input: DuplicateInteractionInput!) {
    platform {
      duplicateInteraction(input: $input) {
        id
      }
    }
  }
`;

export const forwardInteractionMutation = gql`
  mutation forwardInteraction($input: ForwardInteractionInput!) {
    platform {
      forwardInteraction(input: $input) {
        id
      }
    }
  }
`;

export const updateInteractionSettingsMutation = gql`
  mutation updateInteractionSettings($input: UpdateInteractionSettingsInput!) {
    platform {
      updateInteractionSettings(input: $input) {
        id
      }
    }
  }
`;
